import React from 'react';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  TextField,
  IconButton,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Link
} from '@material-ui/core';
import { Fab, Stack, Tooltip } from '@mui/material';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@mui/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import DropzoneArea from 'react-dropzone-uploader';

const useStyles = makeStyles((theme) => ({
  // Ana Container Stilleri
  sectionContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  sectionHeader: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  sectionContent: {
    padding: '24px',
    backgroundColor: '#fffbeb',
  },
  // Upload Area Stilleri
  uploadArea: {
    border: '2px dashed #fde68a',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    padding: '32px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#f59e0b',
      backgroundColor: '#fef3c7',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(245, 158, 11, 0.15)',
    },
  },
  uploadIcon: {
    fontSize: '48px',
    color: '#f59e0b',
    marginBottom: '16px',
  },
  uploadTitle: {
    color: '#334155',
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: '8px',
  },
  uploadSubtitle: {
    color: '#64748b',
    fontSize: '0.875rem',
  },
  // File Item Stilleri
  fileItemCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #fde68a',
    padding: '16px',
    marginBottom: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)',
      borderColor: '#f59e0b',
    },
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  fileIconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileIconPdf: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
  },
  fileIconImage: {
    backgroundColor: '#ecfdf5',
    color: '#059669',
  },
  fileIconDoc: {
    backgroundColor: '#eff6ff',
    color: '#2563eb',
  },
  fileIconDefault: {
    backgroundColor: '#f1f5f9',
    color: '#64748b',
  },
  fileDetails: {
    flex: 1,
  },
  fileName: {
    fontWeight: 600,
    color: '#334155',
    fontSize: '0.95rem',
    marginBottom: '4px',
  },
  fileSize: {
    color: '#64748b',
    fontSize: '0.8rem',
  },
  // Add Button Stilleri
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 0',
    marginTop: '8px',
  },
  addButton: {
    backgroundColor: '#fef3c7',
    color: '#d97706',
    border: '2px dashed #f59e0b',
    borderRadius: '12px',
    padding: '16px 32px',
    fontWeight: 600,
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#f59e0b',
      color: '#ffffff',
      borderStyle: 'solid',
    },
  },
  // Autocomplete Stilleri
  autocompleteField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#ffffff',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    },
  },
  // Delete Button
  deleteButton: {
    backgroundColor: '#fef2f2 !important',
    color: '#dc2626 !important',
    transition: 'all 0.2s ease !important',
    '&:hover': {
      backgroundColor: '#dc2626 !important',
      color: '#ffffff !important',
    },
  },
  // Table Stilleri (Liste görünümü için)
  tableContainer: {
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  tableHead: {
    backgroundColor: '#f8fafc',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.875rem',
      padding: '16px',
      borderBottom: '2px solid #e2e8f0',
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
    '& .MuiTableCell-body': {
      padding: '12px 16px',
      borderBottom: '1px solid #e2e8f0',
    },
  },
  downloadChip: {
    backgroundColor: '#ecfdf5',
    color: '#059669',
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#d1fae5',
    },
  },
  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px dashed #fde68a',
  },
  emptyIcon: {
    fontSize: '64px',
    color: '#fcd34d',
    marginBottom: '16px',
  },
  emptyTitle: {
    color: '#92400e',
    fontWeight: 500,
    fontSize: '1rem',
    marginBottom: '8px',
  },
  emptySubtitle: {
    color: '#b45309',
    fontSize: '0.875rem',
  },
  // Loading State
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
  },
  loadingText: {
    marginTop: '16px',
    color: '#64748b',
    fontWeight: 500,
  },
  // Dropzone Custom Styles
  dropzoneContainer: {
    '& .dzu-dropzone': {
      borderRadius: '10px',
      border: '2px dashed #fde68a',
      backgroundColor: '#ffffff',
      minHeight: '80px',
      overflow: 'hidden',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: '#f59e0b',
        backgroundColor: '#fef3c7',
      },
    },
    '& .dzu-inputLabel': {
      color: '#64748b',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  },
  // Badge for file count
  fileBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
}));

const getFileIcon = (fileName, classes) => {
  if (!fileName) return <InsertDriveFileIcon />;
  
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  if (['pdf'].includes(ext)) {
    return (
      <Box className={`${classes.fileIconWrapper} ${classes.fileIconPdf}`}>
        <PictureAsPdfIcon />
      </Box>
    );
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return (
      <Box className={`${classes.fileIconWrapper} ${classes.fileIconImage}`}>
        <ImageIcon />
      </Box>
    );
  }
  if (['doc', 'docx'].includes(ext)) {
    return (
      <Box className={`${classes.fileIconWrapper} ${classes.fileIconDoc}`}>
        <DescriptionIcon />
      </Box>
    );
  }
  return (
    <Box className={`${classes.fileIconWrapper} ${classes.fileIconDefault}`}>
      <InsertDriveFileIcon />
    </Box>
  );
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Dosya Ekleme Bölümü Componenti (Create için)
export function FileUploadCreate({
  projectFiles,
  setProjectFiles,
  fileTypes,
  data,
  handleFiles,
  handleAddFile,
  checkRolesAuth
}) {
  const classes = useStyles();
  const activeFiles = projectFiles.filter(x => x.active === 1);
  const isDisabled = checkRolesAuth ? !checkRolesAuth('fileTypeWriteAutocomplete') : false;

  const handleDeleteFile = (fileId) => {
    const updatedFiles = projectFiles.map(f =>
      f.id === fileId ? { ...f, active: 0 } : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleFileTypeChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map(f =>
      f.id === fileId
        ? {
            ...f,
            fileType: newValue?.Id,
            fileSystem: newValue?.Id !== 7 ? 0 : f.fileSystem
          }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleSystemChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map(f =>
      f.id === fileId
        ? { ...f, fileSystem: newValue?.Id ? newValue?.Id : 0 }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  return (
    <Box className={classes.sectionContainer}>
      {/* Header */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          <CloudUploadIcon />
          Dosya Ekle
        </Typography>
        {activeFiles.length > 0 && (
          <Box className={classes.fileBadge}>
            {activeFiles.length} dosya
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box className={classes.sectionContent}>
        {activeFiles.length === 0 ? (
          <Box className={classes.emptyState}>
            <CloudUploadIcon className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              Henüz dosya eklenmedi
            </Typography>
            <Typography className={classes.emptySubtitle}>
              Projenize dosya eklemek için aşağıdaki butona tıklayın
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {activeFiles.map((file) => (
              <Paper key={file.id} className={classes.fileItemCard} elevation={0}>
                <Grid container spacing={2} alignItems="center">
                  {/* Dosya Tipi Seçimi */}
                  <Grid item xs={12} sm={(file.fileType === 7 || file.fileType === 4) ? 3 : 5}>
                    <FormControl fullWidth>
                      <Autocomplete
                        disabled={isDisabled}
                        options={fileTypes || []}
                        getOptionLabel={(option) => option.Dsc || ''}
                        value={fileTypes?.find((x) => x.Id === file.fileType) || null}
                        onChange={(event, newValue) => handleFileTypeChange(file.id, newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Dosya Tipi"
                            variant="outlined"
                            size="small"
                            className={classes.autocompleteField}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {/* Bağlı Sistem Seçimi (fileType === 7 veya 4 ise) */}
                  {(file.fileType === 7 || file.fileType === 4) && (
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <Autocomplete
                          disabled={isDisabled}
                          options={
                            data
                              ?.filter((x) => x.Level === 1)
                              .map((item) => ({
                                Id: item.Id,
                                Dsc: item.SystemName
                              })) || []
                          }
                          getOptionLabel={(option) => option.Dsc || ''}
                          value={
                            data?.find((x) => x.Id === file.fileSystem)
                              ? {
                                  Id: file.fileSystem,
                                  Dsc: data.find((x) => x.Id === file.fileSystem)?.SystemName
                                }
                              : null
                          }
                          onChange={(event, newValue) => handleSystemChange(file.id, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Bağlı Sistem"
                              variant="outlined"
                              size="small"
                              className={classes.autocompleteField}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {/* Dosya Yükleme Alanı */}
                  <Grid item xs={12} sm={5}>
                    <Box className={classes.dropzoneContainer}>
                      <DropzoneArea
                        acceptedFiles={['image/*', '.pdf', '.doc']}
                        dropzoneText="Dosya yüklemek için tıklayın veya sürükleyin"
                        onChangeStatus={(files) => handleFiles(files, file.id)}
                        maxFiles={1}
                        minSize={0}
                        maxSize={8072000}
                      />
                    </Box>
                    {file.fileName && (
                      <Box className={classes.fileInfo} sx={{ mt: 1 }}>
                        {getFileIcon(file.fileName, classes)}
                        <Box className={classes.fileDetails}>
                          <Typography className={classes.fileName}>
                            {file.fileName}
                          </Typography>
                          {file.fileDataSize && (
                            <Typography className={classes.fileSize}>
                              {formatFileSize(file.fileDataSize)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  {/* Silme Butonu */}
                  <Grid item xs={12} sm={1}>
                    <Tooltip title="Dosyayı Sil">
                      <Fab
                        size="small"
                        className={classes.deleteButton}
                        onClick={() => handleDeleteFile(file.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Dosya Ekle Butonu */}
        <Box className={classes.addButtonContainer}>
          <Box
            className={classes.addButton}
            onClick={handleAddFile}
            role="button"
            tabIndex={0}
          >
            <AddCircleOutlineIcon />
            Yeni Dosya Ekle
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Dosya Listesi Bölümü Componenti (Update/View için)
export function FileUploadList({
  projectFiles,
  setProjectFiles,
  fileTypes,
  data,
  handleDownload,
  fileLoading,
  checkRolesAuth
}) {
  const classes = useStyles();
  const activeFiles = projectFiles.filter((x) => x.fromDb === 1 && x.active === 1);
  const isDisabled = checkRolesAuth ? !checkRolesAuth('fileTypeReadAutocomplete') : false;

  const handleDeleteFile = (fileId) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId ? { ...f, active: 0 } : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleFileTypeChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? {
            ...f,
            fileType: newValue?.Id,
            fileSystem: newValue?.Id !== 7 ? 0 : f.fileSystem
          }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleSystemChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? { ...f, fileSystem: newValue?.Id ? newValue?.Id : 0 }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  // Loading State
  if (fileLoading) {
    return (
      <Box className={classes.sectionContainer}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            <InsertDriveFileIcon />
            Dosya Listesi
          </Typography>
        </Box>
        <Box className={classes.sectionContent}>
          <Box className={classes.loadingContainer}>
            <CircularProgress sx={{ color: '#f59e0b' }} />
            <Typography className={classes.loadingText}>
              Dosyalar yükleniyor...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.sectionContainer}>
      {/* Header */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          <InsertDriveFileIcon />
          Dosya Listesi
        </Typography>
        {activeFiles.length > 0 && (
          <Box className={classes.fileBadge}>
            {activeFiles.length} dosya
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box className={classes.sectionContent}>
        {activeFiles.length === 0 ? (
          <Box className={classes.emptyState}>
            <InsertDriveFileIcon className={classes.emptyIcon} />
            <Typography className={classes.emptyTitle}>
              Gösterilecek dosya yok
            </Typography>
            <Typography className={classes.emptySubtitle}>
              Bu proje için henüz dosya eklenmemiş
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Dosya Tipi</TableCell>
                  <TableCell>Dosya</TableCell>
                  <TableCell align="center" width={100}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeFiles.map((file) => (
                  <TableRow key={file.id} className={classes.tableRow}>
                    {/* Dosya Tipi */}
                    <TableCell>
                      <Stack spacing={1}>
                        <Autocomplete
                          fullWidth
                          disabled={isDisabled}
                          options={fileTypes || []}
                          getOptionLabel={(option) => option.Dsc || ''}
                          value={fileTypes?.find((x) => x.Id === file.fileType) || null}
                          onChange={(event, newValue) => handleFileTypeChange(file.id, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Dosya Tipi"
                              variant="outlined"
                              size="small"
                              className={classes.autocompleteField}
                            />
                          )}
                        />
                        {(file.fileType === 7 || file.fileType === 4) && (
                          <Autocomplete
                            fullWidth
                            disabled={isDisabled}
                            options={
                              data
                                ?.filter((x) => x.Level === 1)
                                .map((item) => ({
                                  Id: item.Id,
                                  Dsc: item.SystemName
                                })) || []
                            }
                            getOptionLabel={(option) => option.Dsc || ''}
                            value={
                              data?.find((x) => x.Id === file.fileSystem)
                                ? {
                                    Id: file.fileSystem,
                                    Dsc: data.find((x) => x.Id === file.fileSystem)?.SystemName
                                  }
                                : null
                            }
                            onChange={(event, newValue) => handleSystemChange(file.id, newValue)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Bağlı Sistem"
                                variant="outlined"
                                size="small"
                                className={classes.autocompleteField}
                              />
                            )}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {/* Dosya İndirme */}
                    <TableCell>
                      {file.fileData ? (
                        <Chip
                          icon={<DownloadIcon />}
                          label={file.fileName || 'Dosya İndir'}
                          className={classes.downloadChip}
                          onClick={() => handleDownload(file.fileData, file.fileName)}
                        />
                      ) : (
                        <Typography sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                          Dosya bulunamadı
                        </Typography>
                      )}
                    </TableCell>

                    {/* Silme */}
                    <TableCell align="center">
                      <Tooltip title="Dosyayı Sil">
                        <Fab
                          size="small"
                          className={classes.deleteButton}
                          onClick={() => handleDeleteFile(file.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </Fab>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

// Dosya Ekleme Bölümü Componenti (Update için - yeni dosya ekleme)
export function FileUploadAdd({
  projectFiles,
  setProjectFiles,
  fileTypes,
  data,
  handleFiles,
  handleAddFile,
  checkRolesAuth
}) {
  const classes = useStyles();
  const newFiles = projectFiles.filter((x) => x.fromDb === 0 && x.active === 1);
  const isDisabled = checkRolesAuth ? !checkRolesAuth('fileTypeWriteAutocomplete') : false;
  const isButtonDisabled = checkRolesAuth ? !checkRolesAuth('fileIconButton') : false;

  const handleDeleteFile = (fileId) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId ? { ...f, active: 0 } : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleFileTypeChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? {
            ...f,
            fileType: newValue?.Id,
            fileSystem: newValue?.Id !== 7 ? 0 : f.fileSystem
          }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleSystemChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? { ...f, fileSystem: newValue?.Id ? newValue?.Id : 0 }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  return (
    <Box className={classes.sectionContainer}>
      {/* Header */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          <CloudUploadIcon />
          Yeni Dosya Ekle
        </Typography>
        {newFiles.length > 0 && (
          <Box className={classes.fileBadge}>
            {newFiles.length} yeni dosya
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box className={classes.sectionContent}>
        {newFiles.length > 0 && (
          <Stack spacing={2}>
            {newFiles.map((file) => (
              <Paper key={file.id} className={classes.fileItemCard} elevation={0}>
                <Grid container spacing={2} alignItems="center">
                  {/* Dosya Tipi Seçimi */}
                  <Grid item xs={12} sm={(file.fileType === 7 || file.fileType === 4) ? 3 : 5}>
                    <FormControl fullWidth>
                      <Autocomplete
                        disabled={isDisabled}
                        options={fileTypes || []}
                        getOptionLabel={(option) => option.Dsc || ''}
                        value={fileTypes?.find((x) => x.Id === file.fileType) || null}
                        onChange={(event, newValue) => handleFileTypeChange(file.id, newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Dosya Tipi"
                            variant="outlined"
                            size="small"
                            className={classes.autocompleteField}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {/* Bağlı Sistem Seçimi */}
                  {(file.fileType === 7 || file.fileType === 4) && (
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <Autocomplete
                          disabled={isDisabled}
                          options={
                            data
                              ?.filter((x) => x.Level === 1)
                              .map((item) => ({
                                Id: item.Id,
                                Dsc: item.SystemName
                              })) || []
                          }
                          getOptionLabel={(option) => option.Dsc || ''}
                          value={
                            data?.find((x) => x.Id === file.fileSystem)
                              ? {
                                  Id: file.fileSystem,
                                  Dsc: data.find((x) => x.Id === file.fileSystem)?.SystemName
                                }
                              : null
                          }
                          onChange={(event, newValue) => handleSystemChange(file.id, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Bağlı Sistem"
                              variant="outlined"
                              size="small"
                              className={classes.autocompleteField}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {/* Dosya Yükleme Alanı */}
                  <Grid item xs={12} sm={5}>
                    <Box className={classes.dropzoneContainer}>
                      <DropzoneArea
                        acceptedFiles={['image/*', '.pdf', '.doc']}
                        dropzoneText="Dosya yüklemek için tıklayın veya sürükleyin"
                        onChangeStatus={(files) => handleFiles(files, file.id)}
                        maxFiles={1}
                        minSize={0}
                        maxSize={8072000}
                      />
                    </Box>
                    {file.fileName && (
                      <Box className={classes.fileInfo} sx={{ mt: 1 }}>
                        {getFileIcon(file.fileName, classes)}
                        <Box className={classes.fileDetails}>
                          <Typography className={classes.fileName}>
                            {file.fileName}
                          </Typography>
                          {file.fileDataSize && (
                            <Typography className={classes.fileSize}>
                              {formatFileSize(file.fileDataSize)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  {/* Silme Butonu */}
                  <Grid item xs={12} sm={1}>
                    <Tooltip title="Dosyayı Sil">
                      <Fab
                        size="small"
                        className={classes.deleteButton}
                        onClick={() => handleDeleteFile(file.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Dosya Ekle Butonu */}
        <Box className={classes.addButtonContainer}>
          <Box
            className={classes.addButton}
            onClick={isButtonDisabled ? undefined : handleAddFile}
            role="button"
            tabIndex={0}
            style={{ opacity: isButtonDisabled ? 0.5 : 1, cursor: isButtonDisabled ? 'not-allowed' : 'pointer' }}
          >
            <AddCircleOutlineIcon />
            Yeni Dosya Ekle
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default FileUploadCreate;
